import { Flex, Box, AspectRatio, type AspectRatioProps, type FlexProps, type BoxProps } from "@chakra-ui/react";
import React, { type ReactNode } from "react";
import Balancer from "react-wrap-balancer";
import Image, { type StaticImageData } from "next/image";
import { type Theme } from "contexts/color-theme";
import { HeroCTA } from "components/hero";
import { TextTitle } from "ui/text";
import { Section, type SectionProps } from "./section";
import { BookDemoButton } from "./event-tracking";

type HalfHeroProps = {
  theme?: Theme;
  hasBackgroundGradient?: boolean; // Use if the gradient is outside of the component, ex. `/solutions/advertising`
  backgroundGradient?: ReactNode;
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  descriptionAs?: BoxProps["as"];
  primaryCTA?: "signup" | "demo";
  hideSecondary?: boolean; // eslint-disable-line react/boolean-prop-naming
  hideCTA?: boolean; // eslint-disable-line react/boolean-prop-naming
  customImage?: ReactNode;
  image?: StaticImageData | string;
  imageAlt?: string;
  imageObjectFit?: "cover" | "contain";
  imageRatio?: AspectRatioProps["ratio"];
  imageFlex?: BoxProps["flex"];
  containerProps?: FlexProps;
} & SectionProps;

export function HalfHero({
  theme = "light",
  hasBackgroundGradient,
  backgroundGradient,
  eyebrow,
  title,
  description,
  descriptionAs = "h2",
  hideSecondary,
  hideCTA,
  customImage,
  image,
  imageAlt = "", // Default to treating the image as decorative if no descriptive alt text is given
  imageObjectFit = "cover",
  imageRatio = 1 / 1,
  imageFlex = 1,
  containerProps,
  gap = { base: 12, xl: 24 },
  ...props
}: HalfHeroProps) {
  return (
    <Section
      theme={theme}
      pos="relative"
      my={backgroundGradient ?? hasBackgroundGradient ? 0 : { base: 12, md: 16 }}
      py={backgroundGradient ?? hasBackgroundGradient ? { base: 12, md: 16 } : 0}
      overflow="hidden"
      {...props}
    >
      {backgroundGradient}
      <Flex
        align="center"
        justify={{ base: "center", lg: "space-between" }}
        direction={{ base: "column", lg: "row" }}
        gap={gap}
        pos="relative"
        zIndex={backgroundGradient ? 1 : undefined}
        {...containerProps}
      >
        <Box flex={1} textAlign={{ base: "center", lg: "left" }} maxW={{ base: "3xl", lg: "100%" }} mx="auto">
          <TextTitle
            theme={theme}
            size="xl"
            chip={eyebrow ? { text: eyebrow } : undefined}
            titleAs="h1"
            title={<Balancer>{title}</Balancer>}
            description={description}
            descriptionAs={descriptionAs}
            actions={!hideCTA && (hideSecondary ? <BookDemoButton /> : <HeroCTA />)}
          />
        </Box>

        {customImage && customImage}

        {image && (
          <Box
            pos="relative"
            flex={imageFlex}
            w="100%"
            maxW="560px"
            mx={{ base: "auto", lg: undefined }}
            borderRadius="base"
            overflow="hidden"
          >
            <AspectRatio ratio={imageRatio} w="100%">
              <Image
                priority
                fill
                sizes="100%"
                src={typeof image === "string" ? image : image.src}
                alt={imageAlt}
                style={{ objectFit: imageObjectFit }}
              />
            </AspectRatio>
          </Box>
        )}
      </Flex>
    </Section>
  );
}
